import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import BillingStatusBadge from '../../enrollments-billing/components/BillingStatusBadge'
import { enrollmentStatusLabel, formatInvoiceStatusDisplay, formatPaymentStatusDisplay } from '../../../utility/enrollmentsBilling'
import { displayWithCurrency } from '../../../utility/currency'

import { ReactComponent as CalendarIcon } from '../../../assets/images/common/icons/calendar-icon.svg'
import { ReactComponent as GolferIcon } from '../../../assets/images/common/icons/golfer-half-icon.svg'

import drawerClasses from '../../enrollments-billing/components/EnrollmentDetailDrawer.module.scss'
import classes from './MemberEnrollmentCard.module.scss'

// One enrollment in the Members drawer's Enrollments tab: the program and its schedule, then the
// billing line. Clicking it opens the same drill-down the Enrollments & Billing drawer shows.
// The status pill and billing badge come from the billing feature so all three surfaces match.
const MemberEnrollmentCard = ({ enrollment, currency, onSelect }) => {
  const { t } = useTranslation()
  const { course, package: pkg, invoice, displayStatus } = enrollment

  const billingDisplay = invoice ? formatInvoiceStatusDisplay(invoice, t) : formatPaymentStatusDisplay(enrollment.paymentStatus, t)
  const amount = invoice?.total ?? pkg?.price ?? enrollment.amount
  // An enrollment with nothing to bill falls back to an amount of 0; a bare "$0" is noise, so show
  // a price only when there is one to show.
  const hasAmount = amount !== null && amount !== undefined && Number(amount) > 0

  return (
    <button type='button' className={classes.card} onClick={() => onSelect(enrollment)}>
      <div className={classes.header}>
        <span className={classes.programName}>{course?.name}</span>
        <span className={classNames(drawerClasses.statusBadge, drawerClasses[`status_${displayStatus}`])}>
          {enrollmentStatusLabel(displayStatus, t)}
        </span>
      </div>

      {course?.startDate && (
        <span className={classes.metaRow}>
          <CalendarIcon className={classes.metaIcon} />
          <span>
            {moment(course.startDate).format('MMM D, YYYY')}
            {course.endDate && ` - ${moment(course.endDate).format('MMM D, YYYY')}`}
          </span>
        </span>
      )}

      {(enrollment.golfersCount > 0 || course?.meetingTimes) && (
        <span className={classes.metaRow}>
          {enrollment.golfersCount > 0 && (
            <>
              <GolferIcon className={classes.metaIcon} />
              <span className={classes.golferCount}>{enrollment.golfersCount}</span>
            </>
          )}
          {course?.meetingTimes && <span>{course.meetingTimes}</span>}
        </span>
      )}

      {(pkg || hasAmount) && (
        <div className={classes.billingRow}>
          <span className={classes.packageName}>{pkg?.name || t('members.enrollmentsTab.noPackage')}</span>
          <span className={classes.billingRight}>
            {hasAmount && <span className={classes.amount}>{displayWithCurrency(amount, currency)}</span>}
            <BillingStatusBadge display={billingDisplay} />
          </span>
        </div>
      )}
    </button>
  )
}

MemberEnrollmentCard.propTypes = {
  enrollment: PropTypes.object.isRequired,
  currency: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
}

export default MemberEnrollmentCard
